import { Injectable } from '@angular/core';
// import { AppModule } from '../app.module';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, of } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import v4 from 'uuid4';

export interface PraticaModel {
  istitutoGaranzia?: { id: number };
  tipoSegnalazione?: { id: number };
  oggetto: string;
  richiedenti: PersonModel[];
  intestatari: PersonModel[];
  allegatiGenerici: DocumentModel[];
  descQuestione: string;
  descIntrapreso: string;
  recaptcha: string;
  idRelazione: number;
  idRapportoIntestatario: number;
  richiedentiFiles?: any[];
  controparti?: ContropartiModel[];
  idIniziativaSegnalazione?: { id: number };
  consensoPrivacy: boolean;
}

export interface PersonModel {
  idTipoSoggetto: number;
  codiceFiscale: string;
  partitaIva: string;
  cognomeDenominazione: string;
  nome: string;
  sesso: string;
  idCittadinanza: { id: number };
  idStato: number;
  indirizzo: string;
  idProv: number;
  idComune: number;
  cap: string;
  telefono: string;
  altroTelegono: string;
  idDocumentoRiconoscimento: number;
  idDocumentoDelega: number;
  ordineSoggetto: number;
  // idRapportoIntestatario: number;
  personaFisica: boolean;
  principale?: boolean;
}
export interface DocumentModel {
  descrizione: string;
  nomeFile: string;
  idTipoDocumentoIntegrazione: { id: number };
  idFile: string;
}
export interface ContropartiModel {
  denominazione: string;
  indirizzo: string;
  idProv: number;
  idComune: number;
  cap: string;
}
export interface ReturnErrorModel {
  error: boolean;
  richiedenti?: ErrorsModel[];
  intestatari?: ErrorsModel[];
  generali: ErrorsModel[];
  controparti?: ErrorsModel[];
  allegati?: ErrorsModel[];
  status?: number;
}
export interface ErrorsModel {
  key: string;
  message: string;
  order?: number;
  translateOptions?: any;
  error?: string;
}

@Injectable()
export class FrontOfficeService {
  constructor(private http: HttpClient) { }

  getConfigByKey(key: string) {
    return this.http.get(`public/api/config/${key}`);
  }

  getProvince() {
    return this.http.get(`public/api/provincia/all`);
  }

  getProvinceById(idProvince: string | number) {
    return this.http.get(`public/api/provincia/${idProvince}`);
  }

  getComuneById(idComune: string | number) {
    return this.http.get(`public/api/comune/${idComune}`);
  }

  getComuni(idProv: string | number) {
    if (!idProv) return of([]).toPromise();
    return this.http.get(`public/api/comune/prov/${idProv}/all`).toPromise();
  }

  getTipoAllegato() {
    return this.http.get(`public/api/allegato/tipo/all`) as Observable<
        Array<{ id: number; descrizione: string }>
        >;
  }

  getTipoRapportoByIstitutoGaranzia(idIstitutoGaranzia: number | string) {
    if (!idIstitutoGaranzia) return of([]);
    return this.http.get(
        `public/api/rapportoIntestatario/${idIstitutoGaranzia}/all`
    ) as Observable<Array<{ id: number; descrizione: string }>>;
  }

  getLuoghiRestrizione() {
    return this.http.get(`public/api/luoghiRestrizione/all`) as Observable<
        Array<{ id: number; descrizione: string }>
        >;
  }

  getCarceriByLuogoId(idLuogo: number) {
    if (!idLuogo) return of([]);
    return this.http.get(`public/api/luoghiRestrizione/${idLuogo}/carceri/all`) as Observable<Array<{ id: number, descrizione: string }>>;
  }

  getStatesByCitizenshipId(id: number) {
    if (!id) return of([]);
    return this.http.get(`public/api/nazione/stato/${id}/all`) as Observable<Array<{ id: number, nome: string }>>;
  }

  getDownloadLinkForPratica(idIstitutoGaranzia: number) {
    if (idIstitutoGaranzia)
      return `public/api/pratica/download/${idIstitutoGaranzia}`;
  }

  getDownloadFileURLById(idFile: number) {
    if (idFile)
      return `backoffice/allegato/${idFile}/download`;
  }

  getRecaptcha() {
    return this.http.get(`public/api/pratica/recaptcha`) as Observable<any>;
  }

  postConfermaPratica(formData: any, id: number) {
    const headers = new HttpHeaders();
    const richiedentiFiles = {};
    const allegatiFiles = {};
    const intestatariFiles = {};
    const postData: PraticaModel = {
      consensoPrivacy: true,
      tipoSegnalazione: formData.tipoSegnalazione,
      istitutoGaranzia: { id },
      idIniziativaSegnalazione: formData.idIniziativaSegnalazione || null,
      idRelazione: formData.idRelazione,
      idRapportoIntestatario: formData.idRapportoIntestatario,
      oggetto: formData.oggetto,
      richiedenti: formData.richiedenti.map((val, i) => {
        const rndId = v4();
        if (val.documenti && val.documenti.file)
          richiedentiFiles[rndId] = val.documenti.file;
        return this.mapForRequest(val, i, rndId);
      }),
      intestatari: formData.intestatari.map((val, i) => {
        const rndId = v4();
        const idDocumentoGenitore = v4();
        const rndIdDeleghe = v4();
        if (val.documentoGenitore && val.documentoGenitore.file) {
          intestatariFiles[idDocumentoGenitore] = val.documenti.file;
        }
        if (val.documenti && val.documenti.file)
          intestatariFiles[rndId] = val.documenti.file;
        if (val.delega && val.delega.file)
          intestatariFiles[rndIdDeleghe] = val.delega ? val.delega.file : null;
        return this.mapForRequest(
            val,
            i,
            rndId,
            rndIdDeleghe,
            idDocumentoGenitore
        );
      }),
      allegatiGenerici: formData.allegati.map((val, index: number) => {
        const rndId = v4();
        if (val.documenti && val.documenti.file) {
          allegatiFiles[rndId] = val.documenti.file;
          return {
            descrizione: val.documenti.descrizione || null,
            nomeFile: val.documenti.file ? val.documenti.file.name : null,
            idTipoDocumentoIntegrazione: { id: val.documenti.tipo ? parseInt(val.documenti.tipo, 10) : null },
            idFile: rndId || null,
            ordineAllegato: index,
          };
        } else return null;
      }),
      controparti: formData.controparti.map((val, index: number) => {
        return {
          denominazione: val.denominazione,
          personaFisica: false,
          indirizzo: val.indirizzo,
          idProv: { id: val.idProv ? parseInt(val.idProv, 10) : null },
          idComune: { id: val.idComune ? parseInt(val.idComune, 10) : null },
          cap: val.cap,
          email: val.email,
          ordineControparte: index,
        };
      }),
      recaptcha: formData.recaptcha,
      descQuestione: formData.descQuestione,
      descIntrapreso: formData.descIntrapreso,
    };
    const dataToSend = this.jsonToFormData(postData);
    Object.keys(richiedentiFiles).forEach((key) => {
      if (richiedentiFiles[key]) {
        dataToSend.append(
            "richiedentiFiles",
            richiedentiFiles[key],
            key.toString()
        );
      }
    });
    Object.keys(intestatariFiles).forEach((key) => {
      if (intestatariFiles[key]) {
        dataToSend.append(
            "intestatariFiles",
            intestatariFiles[key],
            key.toString()
        );
      }
    });
    Object.keys(allegatiFiles).forEach((key) => {
      if (allegatiFiles[key]) {
        dataToSend.append("allegatiFiles", allegatiFiles[key], key.toString());
      }
    });

    headers.append("Content-Type", "application/form-data");

    let endpoint: string;

    switch (id) {
      case 1: {
        endpoint = "public/api/pratica/garante-infanzia/conferma";
        break;
      }
      case 2: {
        endpoint = "public/api/pratica/garante-detenuti/conferma";
        break;
      }
      case 3: {
        endpoint = "public/api/pratica/difensore-civico/conferma";
        break;
      }
      case 5: {
        endpoint = "public/api/pratica/consigliera-parita/conferma";
        break;
      }
      default: {
        throw new Error("Tipo Pratica non implementato");
      }
    }

    return this.http
        .post(endpoint, dataToSend, { headers })
        .pipe(
            catchError(
                this.handleError<Array<{ id: number; nome: string }>>("post data", [])
            )
        );
  }

  private mapForRequest(
      val: any,
      i: number,
      rndId: string,
      rndIdDeleghe?: string,
      idDocumentoGenitore?: string
  ) {
    console.log(val);
    const returnObj = {
      idTipoSoggetto: { id: val.idTipoSoggetto },
      codiceFiscale: val.codiceFiscale,
      cognomeDenominazione: val.cognomeDenominazione,
      partitaIva: val.partitaIva,
      nome: val.nome,
      sesso: val.sesso,
      idCittadinanza: {
        id: val.idCittadinanza,
      },
      idCittadinanzaAltro: {
        id: val.idCittadinanzaAltro,
      },
      idStato: { id: parseInt(val.idStato, 10) || null },
      indirizzo: val.indirizzo,
      idProv: { id: val.idProv ? parseInt(val.idProv, 10) : null },
      idComune: { id: val.idComune ? parseInt(val.idComune, 10) : null },
      cap: val.cap || null,
      telefono: val.telefono,
      altroTelefono: val.altroTelefono,
      fax: val.fax,
      email: val.email,
      idDocumentoRiconoscimento: val.documenti && val.documenti.file ? {
        descrizione: val.documenti.descrizione || null,
        nomeFile: val.documenti.file ? val.documenti.file.name : null,
        idTipoDocumentoIntegrazione: { id: val.documenti && val.documenti.tipo ? parseInt(val.documenti.tipo, 10) : null },
        idFile: rndId || null,
      } : null,

      idDocumentoDelega: val.delega && val.delega.file ? {
        descrizione: val.delega ? val.delega.descrizione : null,
        nomeFile: val.delega && val.delega.file ? val.delega.file.name : null,
        idTipoDocumentoIntegrazione: { id: val.delega ? parseInt(val.delega.tipo, 10) : null },
        idFile: rndIdDeleghe || null,
      } : null,
      idDocumentoGenitore: val.documentoGenitore && val.documentoGenitore.file ? {
        descrizione: val.documentoGenitore ? val.documentoGenitore.descrizione : null,
        nomeFile: val.documentoGenitore && val.documentoGenitore.file ? val.documentoGenitore.file.name : null,
        idTipoDocumentoIntegrazione: { id: val.documentoGenitore ? parseInt(val.documentoGenitore.tipo, 10) : null },
        idFile: idDocumentoGenitore || null,
      } : null,
      dataNascita: this.convertDate(val.dataNascita || null),
      idProvNascita: {
        id: val.idProvNascita ? parseInt(val.idProvNascita, 10) : null,
      },
      idComuneNascita: {
        id: val.idComuneNascita ? parseInt(val.idComuneNascita, 10) : null,
      },
      idNazionalitaNascita: {
        id: val.idNazionalitaNascita ? val.idNazionalitaNascita : null,
      },
      idStatoNascita: {
        id: val.idStatoNascita ? parseInt(val.idStatoNascita, 10) : null,
      },

      idLuogoRestrizione: {
        id: val.idLuogoRestrizione
            ? parseInt(val.idLuogoRestrizione, 10)
            : null,
      },
      idLuogoRestrizioneCarcere: {
        id: val.idLuogoRestrizioneCarcere
            ? parseInt(val.idLuogoRestrizioneCarcere, 10)
            : null,
      },
      //intDiffuso: val.intDiffuso || null,
      ordineSoggetto: i,
      idRelazione: val.idRelazione && val.idRelazione !== 'null' ? parseInt(val.idRelazione, 10) : null,
      idRapportoIntestatario: { id: val.idRapportoIntestatario && val.idRapportoIntestatario !== 'null' ? parseInt(val.idRapportoIntestatario, 10) : null },
      personaFisica: val.personaFisica,
      principale: val.principale ? val.principale : false,
      copiato: val.copiato ? true : false,
    };
    /*if(rndIdDeleghe){

    }*/
    return returnObj;
  }

  private handleError<T>(operation = "operation", result?: T) {
    return (error: any): Observable<ReturnErrorModel> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // TODO: better job of transforming error for user consumption
      console.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      const richiedentiErrors: ErrorsModel[] = [];
      const intestatariErrors: ErrorsModel[] = [];
      const allegatiErrors: ErrorsModel[] = [];
      const contropartiErrors: ErrorsModel[] = [];
      const generalErrors: ErrorsModel[] = [];
      if (typeof (error.error) !== 'string') Object.keys(error.error).forEach(key => {
        const errorObj: ErrorsModel = { message: null, order: null, key: null };
        errorObj.message = error.error[key];
        // if (key.startsWith('richiedenti') || key.startsWith('intestatari') ||  key.startsWith('alleagti') || key.startsWith('controparti') ) {

        if (key.split('.').length > 1) {
          const splitKey = key.split('.');
          errorObj.key = splitKey[1];
          if (splitKey[0].split('[').length > 1) {
            errorObj.order = parseInt(splitKey[0].split('[')[1].replace(']', ''), 10);
          }
          if (key.startsWith('richiedenti')) {
            richiedentiErrors.push(errorObj);
          } else if (key.startsWith('intestatari')) {
            intestatariErrors.push(errorObj);
          } else if (key.startsWith('allegatiGenerici')) {
            allegatiErrors.push(errorObj);
          } else if (key.startsWith('controparti')) {
            contropartiErrors.push(errorObj);
          }
        } else {
          errorObj.key = key;
          generalErrors.push(errorObj);
        }


      });
      const returnObject: ReturnErrorModel = {
        error: true,
        richiedenti: richiedentiErrors,
        intestatari: intestatariErrors,
        generali: generalErrors,
        allegati: allegatiErrors,
        controparti: contropartiErrors,
      };
      return of(returnObject);
    };
  }

  private buildFormData(formData, data, parentKey?) {
    if (
        data &&
        Array.isArray(data) &&
        !(data instanceof Date) &&
        !(data instanceof File)
    ) {
      Object.keys(data).forEach((key) => {
        this.buildFormData(
            formData,
            data[key],
            parentKey ? `${parentKey}[${key}]` : key
        );
      });
    } else if (
        data &&
        typeof data === "object" &&
        !(data instanceof Date) &&
        !(data instanceof File)
    ) {
      Object.keys(data).forEach((key) => {
        this.buildFormData(
            formData,
            data[key],
            parentKey ? `${parentKey}.${key}` : key
        );
      });
    } else {
      const value = data;

      if (value !== null) formData.append(parentKey, value);
    }
  }

  private jsonToFormData(data) {
    const formData = new FormData();

    this.buildFormData(formData, data);

    return formData;
  }

  private convertDate(inputFormat) {
    if (!inputFormat) return null;
    const pad = (s) => {
      return s < 10 ? "0" + s : s;
    };
    const d = new Date(inputFormat);
    return [pad(d.getDate()), pad(d.getMonth() + 1), d.getFullYear()].join("-");
  }
}
